import React, { useState } from 'react';
import { Form, Input, Button, Typography, Divider, message, Steps } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, TeamOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { authApi } from '../../services/auth';
import { ROUTES } from '../../config/routes';

const { Text, Title } = Typography;
const { Step } = Steps;

export const Register: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();

  const handleSubmit = async (values: {
    email: string;
    password: string;
    confirmPassword: string;
    name: string;
    company_name: string;
  }) => {
    setLoading(true);
    try {
      await authApi.register({
        email: values.email,
        password: values.password,
        name: values.name,
        company_name: values.company_name,
      });
      message.success('注册成功，请登录');
      navigate(ROUTES.LOGIN);
    } catch (error) {
      // 错误已在request拦截器中处理
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: '账号信息',
      content: (
        <>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: '请输入邮箱' },
              {
                validator: (_: unknown, value: string) => {
                  if (!value || /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('请输入有效的邮箱地址'));
                },
              },
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="企业邮箱" />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 8, message: '密码至少8位' },
              {
                pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/,
                message: '密码必须包含字母和数字',
              },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="密码（至少8位，包含字母和数字）" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="确认密码" />
          </Form.Item>
        </>
      ),
    },
    {
      title: '个人信息',
      content: (
        <>
          <Form.Item
            name="name"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="姓名" />
          </Form.Item>

          <Form.Item
            name="company_name"
            rules={[{ required: true, message: '请输入公司名称' }]}
          >
            <Input prefix={<TeamOutlined />} placeholder="公司名称" />
          </Form.Item>
        </>
      ),
    },
  ];

  const next = async () => {
    try {
      if (currentStep === 0) {
        await form.validateFields(['email', 'password', 'confirmPassword']);
      }
      setCurrentStep(currentStep + 1);
    } catch (error) {
      // 验证失败
    }
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  return (
    <>
      <Steps current={currentStep} size="small" style={{ marginBottom: 24 }}>
        {steps.map((item) => (
          <Step key={item.title} title={item.title} />
        ))}
      </Steps>

      <Form
        form={form}
        name="register"
        onFinish={handleSubmit}
        size="large"
        preserve={true}
      >
        {/* 使用 CSS display 切换步骤，确保所有 Form.Item 始终在 DOM 中 */}
        {steps.map((step, index) => (
          <div key={step.title} style={{ display: index === currentStep ? 'block' : 'none' }}>
            {step.content}
          </div>
        ))}

        <Form.Item>
          {currentStep > 0 && (
            <Button style={{ marginRight: 8 }} onClick={prev}>
              上一步
            </Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button type="primary" onClick={next}>
              下一步
            </Button>
          )}
          {currentStep === steps.length - 1 && (
            <Button type="primary" htmlType="submit" loading={loading}>
              注册
            </Button>
          )}
        </Form.Item>
      </Form>

      <Divider />

      <div style={{ textAlign: 'center' }}>
        <Text type="secondary">
          已有账号? <Link to={ROUTES.LOGIN}>立即登录</Link>
        </Text>
      </div>
    </>
  );
};
